import { pick } from 'lodash';
import type {
    EssOsasQuestionnaire,
    Fosq10OsasQuestionnaire,
    IEssOsasQuestionnaire,
    IFosq10OsasQuestionnaire,
    IHsdqCategory,
    IHsdqCategoryWithItems,
    IHsdqOsasQuestionnaire,
    IOsasQuestionnaire,
    IPatientOsasServiceQuestionnaires,
} from '../interfaces/osasQuestionnaire';
import type { LanguageCode } from '../translate/translate';
import { HsdqCategoryName } from '../enums/hsdqCategoryName';
import { OsasQuestionnaireName } from '../enums/osasQuestionnaireName';
import { getLanguage } from '../translate/translate';
import { never } from './never';

export const imsomniaQuestions = [
    'fatigueDuringTheDay',
    'poorSleep',
    'lieAwake',
    'concerned',
    'troublesFallingAsleep',
    'consequencesAfterBadNight',
    'iDontGetEnoughSleep',
    'functionLessWell',
];

export const parasomniaQuestions = [
    'screaming',
    'vividDream',
    'nightmares',
    'sometimesInjured',
    'confusedHalfAwake',
    'sleepWalking',
];

export const crsdQuestions = [
    'difficultyWakingUp',
    'lieAwake',
    'troublesFallingAsleep',
    'normalTime',
    'sleepBadlyDuringDay',
    'timeVaries',
];

export const hypersomnolentieQuestions = [
    'fallAsleepRepeatedly',
    'iDontFeelRestedAfterNap',
    'moreThanTenHours',
    'sleepAttacks',
    'unintentionallyFallAsleepDuringDay',
];

export const rlsLmsQuestions = ['annoyingItchy', 'restlessLegs', 'urgeToMoveLegs', 'moveLegs', 'kickingLegMovements'];

export const sbdQuestions = ['dryMouth', 'stopBreathing', 'snoreLoudly', 'suffocation'];

export const hsdqCriteriums = new Map<string, number>([
    [HsdqCategoryName.insomnia, 3.68],
    [HsdqCategoryName.rlsLms, 2.7],
    [HsdqCategoryName.crsd, 3.41],
    [HsdqCategoryName.hypersomnolentie, 2.9],
    [HsdqCategoryName.parasomnia, 2.42],
    [HsdqCategoryName.sbd, 2.87],
]);

export function getPatientProcedureQuestionnairesForPdf(questionnaires: IOsasQuestionnaire[]) {
    const patientQuestionnaires: IPatientOsasServiceQuestionnaires = {};

    for (const questionnaire of questionnaires) {
        if (questionnaire.name === OsasQuestionnaireName.hsdq) {
            patientQuestionnaires.hsdq = getHsdqQuestionnaireCategoryWithItems(questionnaire.answer);
        } else if (questionnaire.name === OsasQuestionnaireName.fosq10) {
            patientQuestionnaires.fosq10 = getFosq10AnswersSorted(questionnaire.answer);
        } else if (questionnaire.name === OsasQuestionnaireName.ess) {
            patientQuestionnaires.ess = getEssAnswersSorted(questionnaire.answer);
        }
    }

    return patientQuestionnaires;
}

function getHsdqQuestionnaireCategoryWithItems(questionnaire: IHsdqOsasQuestionnaire): IHsdqCategoryWithItems[] {
    const hsdqCategories = getHsdqCategories({ ...questionnaire, name: OsasQuestionnaireName.hsdq });

    const categoriesWithItems: IHsdqCategoryWithItems[] = [];

    for (const category of hsdqCategories) {
        const questions = getHsdqQuestionsByCategory(category.name, questionnaire);

        if (questions) {
            const scoreToQuestions: { [keys: number]: [string] } = {};

            Object.keys(questions).forEach((question) => {
                const score: number = (questions as any)[question];
                scoreToQuestions[score]
                    ? scoreToQuestions[score].push(question)
                    : (scoreToQuestions[score] = [question]);
            });

            const sortedKeys = Object.keys(scoreToQuestions)
                .sort()
                .reverse()
                .map((a) => parseInt(a));
            const categoryItems = new Map(sortedKeys.map((key) => [key, scoreToQuestions[key]]));

            categoriesWithItems.push({
                ...category,
                items: categoryItems,
            });
        }
    }

    return categoriesWithItems;
}

function getFosq10AnswersSorted(questionnaire: IFosq10OsasQuestionnaire): Fosq10OsasQuestionnaire {
    const questionnaireToArray: [string, string][] = Object.keys(questionnaire).map((question) => [
        question,
        ((questionnaire as any)[question] || 'NVT').toString(),
    ]);

    return new Map(questionnaireToArray.sort((a, b) => a[1].localeCompare(b[1])));
}

function getEssAnswersSorted(questionnaire: IEssOsasQuestionnaire): EssOsasQuestionnaire {
    const questionnaireToArray: [string, number][] = Object.keys(questionnaire).map((question) => [
        question,
        (questionnaire as any)[question],
    ]);

    return new Map(questionnaireToArray.sort((a, b) => b[1] - a[1]));
}

export function getHsdqCategories(hsdqQuestionnaire: any) {
    const categories: IHsdqCategory[] = Object.keys(HsdqCategoryName).map((key) => {
        const category: HsdqCategoryName = (HsdqCategoryName as any)[key];
        const score = calculateHsdqScoreForCategory(hsdqQuestionnaire, category);

        return {
            criteria: hsdqCriteriums.get(category)!,
            name: category,
            overrun: score! > hsdqCriteriums.get(category)! ? 'Ja' : 'Nee',
            score: Number(score!.toFixed(2)),
        };
    });

    return categories
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
        .sort((a, b) =>
            a.overrun === 'Ja' && b.overrun === 'Nee' ? -1 : a.overrun === 'Nee' && b.overrun === 'Ja' ? 1 : 0
        );
}

export function getHsdqQuestionsByCategory(category: string, hsdqQuestionnaire: IHsdqOsasQuestionnaire) {
    switch (category) {
        case HsdqCategoryName.insomnia:
            return pick(hsdqQuestionnaire, imsomniaQuestions);
        case HsdqCategoryName.rlsLms:
            return pick(hsdqQuestionnaire, rlsLmsQuestions);
        case HsdqCategoryName.crsd:
            return pick(hsdqQuestionnaire, crsdQuestions);
        case HsdqCategoryName.hypersomnolentie:
            return pick(hsdqQuestionnaire, hypersomnolentieQuestions);
        case HsdqCategoryName.parasomnia:
            return pick(hsdqQuestionnaire, parasomniaQuestions);
        case HsdqCategoryName.sbd:
            return pick(hsdqQuestionnaire, sbdQuestions);
        default:
            return;
    }
}

export function getHsdqCategoryByQuestion(prefix: string) {
    if (imsomniaQuestions.includes(prefix)) {
        return HsdqCategoryName.insomnia;
    }
    if (rlsLmsQuestions.includes(prefix)) {
        return HsdqCategoryName.rlsLms;
    }
    if (crsdQuestions.includes(prefix)) {
        return HsdqCategoryName.crsd;
    }
    if (hypersomnolentieQuestions.includes(prefix)) {
        return HsdqCategoryName.hypersomnolentie;
    }
    if (parasomniaQuestions.includes(prefix)) {
        return HsdqCategoryName.parasomnia;
    }
    if (sbdQuestions.includes(prefix)) {
        return HsdqCategoryName.sbd;
    }

    return '';
}

export function getHsdqAnswerByValue(number: number, language: LanguageCode) {
    const translate = getLanguage(language);
    switch (number) {
        case 1:
            return `1 - ${translate.osasQuestionnaires.notAtAll}`;
        case 2:
            return `2 - ${translate.osasQuestionnaires.usuallyNot}`;
        case 3:
            return `3 - ${translate.osasQuestionnaires.sometimes}`;
        case 4:
            return `4 - ${translate.osasQuestionnaires.usuallyYes}`;
        case 5:
            return `5 - ${translate.osasQuestionnaires.absolutely}`;
        default:
            return;
    }
}

export function getOsasQuestionnaireScore(osasQuestionnaire: IOsasQuestionnaire) {
    let scoresForCategories: number[];
    switch (osasQuestionnaire.name) {
        case OsasQuestionnaireName.fosq10:
            scoresForCategories = calculateScoresForCategories(osasQuestionnaire.answer);

            return Math.round(
                (scoresForCategories.reduce((acc, current) => acc + current, 0) / scoresForCategories.length) * 5
            );
        case OsasQuestionnaireName.ess:
            return (
                osasQuestionnaire.answer.whenReading! +
                osasQuestionnaire.answer.whenWatchingTv! +
                osasQuestionnaire.answer.inAPublicEnvironment! +
                osasQuestionnaire.answer.sittingInACarAsPassenger! +
                osasQuestionnaire.answer.restingInTheAfternoon! +
                osasQuestionnaire.answer.talkingToSomeone! +
                osasQuestionnaire.answer.afterLunch! +
                osasQuestionnaire.answer.waitingInTheCarInTraffic!
            );
        case OsasQuestionnaireName.hsdq:
            return getScoreForHsdqOptions([
                osasQuestionnaire.answer.fatigueDuringTheDay,
                osasQuestionnaire.answer.annoyingItchy,
                osasQuestionnaire.answer.dryMouth,
                osasQuestionnaire.answer.screaming,
                osasQuestionnaire.answer.difficultyWakingUp,
                osasQuestionnaire.answer.restlessLegs,
                osasQuestionnaire.answer.poorSleep,
                osasQuestionnaire.answer.urgeToMoveLegs,
                osasQuestionnaire.answer.moveLegs,
                osasQuestionnaire.answer.lieAwake,
                osasQuestionnaire.answer.kickingLegMovements,
                osasQuestionnaire.answer.concerned,
                osasQuestionnaire.answer.troublesFallingAsleep,
                osasQuestionnaire.answer.consequencesAfterBadNight,
                osasQuestionnaire.answer.iDontGetEnoughSleep,
                osasQuestionnaire.answer.vividDream,
                osasQuestionnaire.answer.stopBreathing,
                osasQuestionnaire.answer.snoreLoudly,
                osasQuestionnaire.answer.suffocation,
                osasQuestionnaire.answer.nightmares,
                osasQuestionnaire.answer.functionLessWell,
                osasQuestionnaire.answer.sometimesInjured,
                osasQuestionnaire.answer.fallAsleepRepeatedly,
                osasQuestionnaire.answer.confusedHalfAwake,
                osasQuestionnaire.answer.iDontFeelRestedAfterNap,
                osasQuestionnaire.answer.normalTime,
                osasQuestionnaire.answer.sleepBadlyDuringDay,
                osasQuestionnaire.answer.moreThanTenHours,
                osasQuestionnaire.answer.sleepAttacks,
                osasQuestionnaire.answer.timeVaries,
                osasQuestionnaire.answer.sleepWalking,
                osasQuestionnaire.answer.unintentionallyFallAsleepDuringDay,
            ]);
        default:
            return;
    }
}

export function calculateHsdqScoreForCategory(answer: any, category: HsdqCategoryName) {
    switch (category) {
        case HsdqCategoryName.insomnia:
            return getScoreForHsdqOptions([
                answer.fatigueDuringTheDay,
                answer.poorSleep,
                answer.lieAwake,
                answer.concerned,
                answer.troublesFallingAsleep,
                answer.consequencesAfterBadNight,
                answer.iDontGetEnoughSleep,
                answer.functionLessWell,
            ]);
        case HsdqCategoryName.parasomnia:
            return getScoreForHsdqOptions([
                answer.screaming,
                answer.vividDream,
                answer.nightmares,
                answer.sometimesInjured,
                answer.confusedHalfAwake,
                answer.sleepWalking,
            ]);
        case HsdqCategoryName.rlsLms:
            return getScoreForHsdqOptions([
                answer.annoyingItchy,
                answer.restlessLegs,
                answer.urgeToMoveLegs,
                answer.moveLegs,
                answer.kickingLegMovements,
            ]);
        case HsdqCategoryName.crsd:
            return getScoreForHsdqOptions([
                answer.difficultyWakingUp,
                answer.lieAwake,
                answer.troublesFallingAsleep,
                answer.normalTime,
                answer.sleepBadlyDuringDay,
                answer.timeVaries,
            ]);
        case HsdqCategoryName.hypersomnolentie:
            return getScoreForHsdqOptions([
                answer.fallAsleepRepeatedly,
                answer.iDontFeelRestedAfterNap,
                answer.moreThanTenHours,
                answer.sleepAttacks,
                answer.unintentionallyFallAsleepDuringDay,
            ]);
        case HsdqCategoryName.sbd:
            return getScoreForHsdqOptions([
                answer.dryMouth,
                answer.stopBreathing,
                answer.snoreLoudly,
                answer.suffocation,
            ]);
        default:
            never(category);
    }
}

function calculateScoresForCategories(answer: any) {
    const scoresForCategories: number[] = [];

    const generalProductivityScore = getScoreForHsdqOptions([
        answer.difficultyConcentratingOnWork!,
        answer.difficultyRememberingThings!,
    ]);
    if (generalProductivityScore) {
        scoresForCategories.push(generalProductivityScore);
    }

    const activityLevelScore = getScoreForHsdqOptions([
        answer.haveRelationshipsDeteriorated!,
        answer.difficultyBeingActiveAtNight!,
        answer.difficultyBeingActiveInTheMorning!,
    ]);
    if (activityLevelScore) {
        scoresForCategories.push(activityLevelScore);
    }

    const vigilanceLevelScore = getScoreForHsdqOptions([
        answer.difficultyDrivingAVehicleShortDistances!,
        answer.difficultyDrivingAVehicleLongDistances!,
        answer.difficultyWatchingVideoOrFilm!,
    ]);
    if (vigilanceLevelScore) {
        scoresForCategories.push(vigilanceLevelScore);
    }

    if (answer.difficultyVisitingFriendsAndFamily! > 0) {
        scoresForCategories.push(answer.difficultyVisitingFriendsAndFamily!);
    }

    if (answer.needForIntimacy! > 0) {
        scoresForCategories.push(answer.needForIntimacy);
    }

    return scoresForCategories;
}

function getScoreForHsdqOptions(options: number[]) {
    const validOptions = options.filter((option) => option !== 0);

    let score;
    if (validOptions.length > 0) {
        score = validOptions.reduce((acc, current) => acc + current, 0) / validOptions.length;
    }

    return score;
}
